import React, {useState, useCallback} from 'react';
import {View, StyleSheet, FlatList, TouchableOpacity, RefreshControl, ActivityIndicator} from 'react-native';
import {Text, Snackbar} from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useFocusEffect, useNavigation} from '@react-navigation/native';
import {workerApi} from '../../services/api/workerApi';
import {colors, spacing} from '../../theme';

const TaskAssignmentsScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const response = await workerApi.getTaskAssignments();
      setData(response.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load task assignments');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData]),
  );

  const handleRowPress = item => {
    navigation.navigate('TaskAssignmentDetail', {
      workerId: item.workerId,
      workerName: item.workerName,
      taskTypeId: item.taskTypeId,
      taskTypeName: item.taskTypeName,
    });
  };

  const renderItem = ({item}) => (
    <TouchableOpacity
      activeOpacity={0.7}
      style={styles.card}
      onPress={() => handleRowPress(item)}>
      <View style={styles.cardLeft}>
        <View style={styles.avatar}>
          <Text style={styles.avatarText}>
            {item.workerName?.charAt(0)?.toUpperCase() || '?'}
          </Text>
        </View>
      </View>
      <View style={styles.cardBody}>
        <Text style={styles.workerName} numberOfLines={1}>{item.workerName}</Text>
        <View style={styles.tagsRow}>
          <View style={styles.roleTag}>
            <MaterialCommunityIcons name="account-outline" size={12} color={colors.textSecondary} />
            <Text style={styles.tagText}>{item.workerRole}</Text>
          </View>
          <View style={styles.taskTag}>
            <MaterialCommunityIcons name="clipboard-text-outline" size={12} color={colors.primary} />
            <Text style={[styles.tagText, {color: colors.primary}]}>{item.taskTypeName}</Text>
          </View>
        </View>
      </View>
      <View style={styles.cardRight}>
        <View style={styles.jobsBadge}>
          <Text style={styles.jobsCount}>{item.jobCount}</Text>
        </View>
        <Text style={styles.jobsLabel}>jobs</Text>
      </View>
      <MaterialCommunityIcons name="chevron-right" size={20} color={colors.textLight} />
    </TouchableOpacity>
  );

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <FlatList
        data={data}
        keyExtractor={(item, index) => `${item.workerId}-${item.taskTypeId}-${index}`}
        renderItem={renderItem}
        contentContainerStyle={styles.listContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={() => fetchData(true)} colors={[colors.primary]} />
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <MaterialCommunityIcons name="clipboard-check-outline" size={48} color={colors.textLight} />
            <Text style={styles.emptyText}>{t('common.noData')}</Text>
          </View>
        }
      />

      <Snackbar
        visible={!!error}
        onDismiss={() => setError('')}
        duration={3000}>
        {error}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: colors.background},
  listContent: {padding: spacing.sm, paddingBottom: spacing.xl, gap: spacing.sm},

  // Card
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: spacing.md,
    gap: spacing.sm,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  cardLeft: {},
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary + '15',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {fontSize: 16, fontWeight: '700', color: colors.primary},
  cardBody: {flex: 1},
  workerName: {fontSize: 15, fontWeight: '600', color: colors.text},
  tagsRow: {flexDirection: 'row', flexWrap: 'wrap', marginTop: 4, gap: 6},
  roleTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    backgroundColor: colors.background,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  taskTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    backgroundColor: colors.primary + '10',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  tagText: {fontSize: 11, color: colors.textSecondary, fontWeight: '500'},
  cardRight: {alignItems: 'center', marginRight: 2},
  jobsBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  jobsCount: {fontSize: 14, fontWeight: '800', color: colors.white},
  jobsLabel: {fontSize: 10, color: colors.textSecondary, marginTop: 2},

  // Empty
  emptyContainer: {
    alignItems: 'center',
    paddingTop: spacing.xxl * 2,
  },
  emptyText: {
    color: colors.textSecondary,
    marginTop: spacing.sm,
    fontSize: 14,
  },
});

export default TaskAssignmentsScreen;
